// Shared events domain client.
//
// Owns the `/api/events/*` paths so web, mobile, and Guardian stop hand-copying
// them. App-specific concerns stay app-side via the adapter: cache adapters,
// the raw-contract join/leave handling, notification side effects, and the
// non-event paths used by event screens (`/api/upload/*`, `/api/venue-requests/*`)
// which belong to other domains.
//
// Bodies are passed by the caller where they diverge between clients -- notably
// join: web sends an attendee body, mobile sends none. Bodies that are
// identical across clients (hosts) are built here. Host requests use the
// authenticated principal; the server ignores request bodies.
//
// The adapter mirrors the createApiClient method surface:
//   get(path, options), post(path, body, options),
//   patch(path, body, options), put(path, body, options), delete(path, options)

import { readEventHostsRequest, readEventImageRequest, readEventWriteRequest, readEventListRequest, readEventIdentifier } from '../event-requests.js';
export { EventRequestContractError, readEventHostsRequest, readEventImageRequest, readEventWriteRequest, readEventListRequest, readEventIdentifier } from '../event-requests.js';
export {readOccurrenceReadContext, occurrenceCalendarRequestScope, assertOccurrenceResponseScope} from '../occurrence-read-contract.js';

export const EVENT_PATHS = {
  collection: '/api/events',
  create: '/api/events/create',
};

export const buildEventPath = (id) =>
  `/api/events/${encodeURIComponent(id)}`;

export const buildEventUpdatePath = (id) =>
  `/api/events/${encodeURIComponent(id)}/update`;

export const buildEventImagePath = (id) =>
  `/api/events/${encodeURIComponent(id)}/image`;

export const buildEventJoinPath = (id) =>
  `/api/events/${encodeURIComponent(id)}/join`;

export const buildEventLeavePath = (id) =>
  `/api/events/${encodeURIComponent(id)}/leave`;

export const buildEventHostsPath = (id) =>
  `/api/events/${encodeURIComponent(id)}/hosts`;

export const buildEventMaterialPath = (id, materialId) =>
  `/api/events/${encodeURIComponent(id)}/materials/${encodeURIComponent(materialId)}`;

export const buildEventHostRequestsPath = (id) =>
  `/api/events/${encodeURIComponent(id)}/hostRequests`;

export const buildEventHostRequestDecisionPath = (id, requesterId, decision) =>
  `/api/events/${encodeURIComponent(id)}/hostRequests/${encodeURIComponent(requesterId)}/${decision}`;

export const buildAttendeeEventsPath = (userId) =>
  `/api/events/user/${encodeURIComponent(userId)}/attending`;

export const createEventsClient = (adapter) => {
  if (!adapter || typeof adapter !== 'object') {
    throw new TypeError('createEventsClient requires a transport adapter.');
  }

  return {
    list(params = {}, options = {}) {
      return adapter.get(EVENT_PATHS.collection, { ...options, params: readEventListRequest(params) });
    },

    listForAttendee(userId, options = {}) {
      const regional = options.params && Object.hasOwn(options.params, 'displayTimeZone');
      return adapter.get(buildAttendeeEventsPath(readEventIdentifier(userId)), regional
        ? {...options, params: readEventListRequest(options.params)} : options);
    },

    getById(id, options = {}) {
      return adapter.get(buildEventPath(readEventIdentifier(id)), options);
    },

    create(payload, options = {}) {
      return adapter.post(EVENT_PATHS.create, readEventWriteRequest(payload, 'create'), options);
    },

    update(id, updates, options = {}) {
      return adapter.patch(buildEventUpdatePath(readEventIdentifier(id)), readEventWriteRequest(updates, 'update'), options);
    },

    setImage(id, body, options = {}) {
      return adapter.patch(buildEventImagePath(readEventIdentifier(id)), readEventImageRequest(body), options);
    },

    // Bodies diverge between clients, so the caller supplies it.
    join(id, body = null, options = {}) {
      return adapter.put(buildEventJoinPath(readEventIdentifier(id)), body, options);
    },

    leave(id, body = null, options = {}) {
      return adapter.put(buildEventLeavePath(readEventIdentifier(id)), body, options);
    },

    setHosts(id, hosts, options = {}) {
      return adapter.patch(buildEventHostsPath(readEventIdentifier(id)), readEventHostsRequest(hosts), options);
    },

    remove(id, options = {}) {
      return adapter.delete(buildEventPath(readEventIdentifier(id)), options);
    },

    removeMaterial(id, materialId, options = {}) {
      return adapter.delete(buildEventMaterialPath(readEventIdentifier(id), readEventIdentifier(materialId)), options);
    },

    requestHost(id, options = {}) {
      return adapter.put(
        buildEventHostRequestsPath(readEventIdentifier(id)),
        null,
        options,
      );
    },

    decideHostRequest(id, requesterId, decision, options = {}) {
      if (!['accept', 'decline'].includes(decision)) {
        throw new TypeError('Event host request decision must be accept or decline.');
      }
      return adapter.put(buildEventHostRequestDecisionPath(readEventIdentifier(id), readEventIdentifier(requesterId), decision), null, options);
    },
  };
};

export { readEventListResponse, readEventDetailResponse, readEventMutationResponse, readEventWrappedResponse, readEventMessageResponse, readEventHostRequestsResponse, readEventHostAcceptanceResponse, readEventMaterialsResponse, EventResponseContractError } from '../event-responses.js';
